import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom, forkJoin, from, Observable, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/auth.service';
import { ProductCategoryEnum } from 'src/app/core/enums/product-category.enum';
import { ProductCategoryService } from 'src/app/services/product-category.service';

export interface CreateStorageRequest {
  name: string;
  address: string;
  centerId?: number;
  centerGroupId?: number;
  allowedCategoryIds?: string[];
  allowedSubCategoryIds?: string[];
}

export interface UpdateStorageRequest {
  name: string;
  address: string;
  centerId?: number;
  centerGroupId?: number;
  allowedCategoryIds?: string[];
  allowedSubCategoryIds?: string[];
}

export interface StorageResponse {
  id: number;
  name: string;
  address: string;
  centerId: number | null;
  centerGroupId?: number | null;
  creationDate: string;
  modificationDate?: string;
  allowedCategoryIds?: ProductCategoryEnum[];
  allowedSubCategoryIds: string[];
}

@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly baseUrl = environment.cafler_backoffice_back_url;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly productCategoryService: ProductCategoryService
  ) { }

  getAllStorages(centerId?: number): Observable<StorageResponse[]> {
    const params = this.buildCenterParams(centerId);

    return this.withAuthHeaders(headers =>
      this.http.get<StorageResponse[]>(this.endpoint('/stores'), { headers, params }).pipe(
        switchMap((storages: StorageResponse[]) =>
          forkJoin(
            storages.map(store =>
              this.productCategoryService
                .getCategoriesBySubcategoryIds(store.allowedSubCategoryIds)
                .pipe(
                  map(categories => ({
                    ...store,
                    allowedCategoryIds: categories.map((category) => { return category.productCategoryId })
                  }))
                )
            )
          )
        )
      )
    );
  }

  createStorage(payload: CreateStorageRequest): Observable<StorageResponse> {
    return this.withAuthHeaders((headers) =>
      this.http.post<StorageResponse>(this.endpoint('/stores'), payload, { headers })
    );
  }

  editStorage(id: string, payload: UpdateStorageRequest): Observable<StorageResponse> {
    return this.withAuthHeaders((headers) =>
      this.http.put<StorageResponse>(this.endpoint(`/stores/${encodeURIComponent(id)}`), payload, { headers })
    );
  }

  deleteStorage(id: string): Observable<void> {
    return this.withAuthHeaders((headers) =>
      this.http.delete<void>(this.endpoint(`/stores/${encodeURIComponent(id)}`), { headers })
    );
  }

  private endpoint(path: `/${string}`): string {
    return `${this.baseUrl}${path}`;
  }

  private buildCenterParams(centerId?: number): HttpParams {
    if (!centerId || centerId <= 0) {
      return new HttpParams();
    }
    return new HttpParams().set('centerId', String(centerId));
  }

  private withAuthHeaders<T>(request: (headers: HttpHeaders) => Observable<T>): Observable<T> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No token available'));
        }

        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
        });

        return request(headers);
      })
    );
  }
}
